import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  CircularProgress,
  Alert,Typography,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  TrendingUp as MetricsIcon,
  CalendarToday as CalendarIcon,
  Flag as PriorityIcon,
  Timeline as ProgressIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Warning as OverdueIcon,
  PlayArrow as InProgressIcon,
  TrackChanges as TargetIcon

} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function SmartGoals() {
  const [smartGoals, setSmartGoals] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);

  // Fetch current user to get employee_id
  const fetchCurrentUser = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  // Fetch goals for the current employee
  const fetchGoals = async (empId) => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/goals/employee/${empId}`,  {
        headers: {
          Authorization: `Bearer ${token}`,
          
        }
      });
      setSmartGoals(response.data);
    } catch (err) {
      //console.error("Error fetching goals:", err);
      setError('Failed to fetch goals');
    }
  };

  // Initialize component - fetch user and goals
  useEffect(() => {
    const initializeComponent = async () => {
      setLoading(true);
      const empId = await fetchCurrentUser();
      if (empId) {
        await fetchGoals(empId);
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.company_id) {
            const res = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data && res.data.data) {
              setCompanyConfig(res.data.data);
            }
          }
        } catch (e) {}
      }
      setLoading(false);
    };

    initializeComponent();
  }, []);

  // Get status color and icon
  const getStatusInfo = (goal) => {
    const dueDate = new Date(goal.dueDate);
    const now = new Date();
    const isOverdue = dueDate < now && goal.status !== 'Completed';

    if (goal.status === 'Completed') {
      return { color: 'success', icon: <CompletedIcon />, label: 'Completed' };
    } else if (isOverdue) {
      return { color: 'error', icon: <OverdueIcon />, label: 'Overdue' };
    } else if (goal.status === 'In Progress' || (goal.progress && goal.progress > 0)) {
      return { color: 'warning', icon: <InProgressIcon />, label: 'In Progress' };
    } else {
      return { color: 'default', icon: <PendingIcon />, label: 'Not Started' };
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get border color based on priority
  const getBorderColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    if (companyConfig?.timezone) {
      options.timeZone = companyConfig.timezone;
    }
    return date.toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <Card sx={{ height: '450px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid', borderColor: 'grey.300', boxShadow: 2 }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card sx={{ height: '450px', overflow: 'auto', border: '1px solid', borderColor: 'grey.300', boxShadow: 2 }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          px: 3,
          py: 2,
          borderBottom: '1px solid #eee',
        }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
            <TaskIcon sx={{ mr: 1, color: '#4caf50' }} />
            My Smart Goals
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Goals List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {smartGoals.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
              <TargetIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Goals Found
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Your assigned SMART goals will appear here.
              </Typography>
            </Box>
          ) : (
            smartGoals.map((goal) => {
              const statusInfo = getStatusInfo(goal);
              
              return (
                <Box key={goal.id} sx={{ mb: 2 }}>
                  <Card 
                    sx={{ 
                      borderLeft: `4px solid ${getBorderColor(goal.priority)}`,
                      boxShadow: 1,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      {/* Header with Title and Status/Priority buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography 
                          variant="body1" 
                          component="div" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#333',
                            flexGrow: 1,
                            mr: 2
                          }}
                        >
                         <Box component="span" sx={{ fontWeight: 'bold' }}>Goal:</Box> {goal.title}
                        </Typography>
                        
                        {/* Priority and Status chips on the right */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {goal.priority && (
                            <Chip
                              label={goal.priority}
                              color={getPriorityColor(goal.priority)}
                              size="small"
                              sx={{ 
                                fontSize: '11px',
                                height: '20px',
                                borderRadius: '10px'
                              }}
                            />
                          )}
                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.color}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              height: '20px',
                             
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Metrics */}
                      {goal.metrics && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            
                            fontSize: '13px',
                            mb: 1
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 'bold' }}>Metrics:</Box> {goal.metrics}
                        </Typography>
                      )}

                      {/* Due Date in single line */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          
                          fontSize: '13px',
                          mb: 2
                        }}
                      >
                         <Box component="span" sx={{ fontWeight: 'bold' }}>Due:</Box> {formatDate(goal.dueDate)}
                      </Typography>

                      {/* Progress Bar */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={goal.progress || 0}
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: statusInfo.color === 'success' ? '#4caf50' : 
                                               statusInfo.color === 'warning' ? '#ff9800' :
                                               statusInfo.color === 'error' ? '#f44336' : '#2196f3'
                              }
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#666',
                            fontSize: '11px',
                            minWidth: '60px',
                            textAlign: 'right'
                          }}
                        >
                          Progress: {goal.progress || 0}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })
          )}
        </Box>
      </CardContent>
    </Card>
  );
}